import { NOTIFICATION_TYPES } from '@optimizely/optimizely-sdk';
const activeExperiments = [];
const listenerMap = new Map();

// These need to be hoisted up outside the component so that every server render doesn't create a new method and add it
// as a new listener.  By putting them at the top level optimizely will detect that it's the same function being added
// each time and skip a new registration.
const onActivate = activationEvent => {
  const {
    experiment = {},
    variation = {}
  } = activationEvent;
  if (experiment.key && variation.key) {
    if (!activeExperiments.includes(experiment.key + ':' + variation.key)) {
      activeExperiments.push(experiment.key + ':' + variation.key);
    }
  }
};
const onDecision = (experimentIdMap, decisionEvent) => {
  const {
    decisionInfo = {},
    type
  } = decisionEvent;
  if (type === 'flag') {
    const {
      experimentId,
      variationKey
    } = decisionInfo;
    const experiment = experimentIdMap?.[experimentId];
    if (experiment && variationKey) {
      const {
        key: experimentKey
      } = experiment;
      // HAVE to make sure that variation key is not null.  A/B tests in optimizely still create a decision
      // even if you aren't in a qualifying audience.  Feature tests don't do this though???
      if (!activeExperiments.includes(experimentKey + ':' + variationKey)) {
        activeExperiments.push(experimentKey + ':' + variationKey);
      }
    }
  }
};

/**
 * Registers a notification listener if it hasn't already been registered in cache
 * @param {Object} optimizely - The optimizely instance
 * @param {string} type - The notification type
 * @param {Function} handler - The handler function
 * @param {boolean} forceRegister - If true, will register the listener even if it already exists in cache
 */
const registerNotificationListener = function (optimizely, type, handler, _temp) {
  let {
    identifier = '{}'
  } = _temp === void 0 ? {} : _temp;
  if (!optimizely?.notificationCenter) return;

  // Check if we already have a listener for this type
  const existing = listenerMap.get(type);
  const forceRegister = existing?.identifier !== identifier;
  if (forceRegister && existing?.notificationId) {
    optimizely.notificationCenter.removeNotificationListener(existing.notificationId);
  }
  if (forceRegister || !existing) {
    const experimentIdMap = JSON.parse(identifier || '{}');
    handler = handler.bind(null, experimentIdMap);
    const notificationId = optimizely.notificationCenter.addNotificationListener(type, handler);
    listenerMap.set(type, {
      notificationId,
      handler,
      identifier
    });
  }
};
const clearAllListeners = optimizely => {
  if (optimizely?.notificationCenter) {
    listenerMap.forEach(_ref => {
      let {
        notificationId
      } = _ref;
      optimizely.notificationCenter.removeNotificationListener(notificationId);
    });
    listenerMap.clear();
  }
};
const OptimizelyTestDecisionListener = (optimizely, listenerConfig) => {
  if (optimizely) {
    const {
      experimentsMap
    } = optimizely.getOptimizelyConfig();
    const values = Object.values(experimentsMap || {});
    const experimentIdMap = Object.fromEntries(values.map(exp => [exp.id, exp]));
    const identifier = JSON.stringify(experimentIdMap);
    registerNotificationListener(optimizely, NOTIFICATION_TYPES.DECISION, onDecision, {
      identifier
    });
    registerNotificationListener(optimizely, NOTIFICATION_TYPES.ACTIVATE, onActivate);

    // For logging when the listener is registered
    listenerConfig?.onRegistered?.(optimizely.notificationCenter);
  }
};
const getActiveExperiments = () => activeExperiments;
export { getActiveExperiments, clearAllListeners };
export default OptimizelyTestDecisionListener;